// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The shell every route sits in (M3-37, 08 §3).
//
// A three-row grid: the top bar, the route, and the navigation. The middle row is the only thing
// that scrolls, which is what stops a phone rubber-banding the whole page while a ball is in
// play and what keeps the bottom bar where a thumb expects it.
//
// On a desktop the bar becomes a header and the bottom row is not rendered at all — not hidden.
// A hidden nav is still in the accessibility tree, so a keyboard user tabs through five
// destinations twice.

import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'
import { BottomNav } from './BottomNav'
import { TopBar } from './TopBar'
import { navPlacement } from '../navigation'
import { setPlayFocus } from '../playFocus'
import { usePlayFocus } from '../usePlayFocus'
import { isMachineRoute, titleFor } from '../routes'
import { isUnknownGuide, knownPageMeta } from '../machineGuides'
import { canonicalFor } from '../../seo/canonical'
import { copyFor } from '../../seo/pageCopy'
import { usePageMeta } from '../usePageMeta'
import { SiteFooter } from './SiteFooter'
import { useViewportWidth } from '../useViewport'
import type { ThemeController } from '../theme'
import { useLocalisation } from '../i18n/useLanguage'

/**
 * Write one head element, creating it only if it is not already there.
 *
 * **The prerendered pages already carry all four of these** (S2-04), so on a first load this
 * updates rather than duplicates — two canonical links or two descriptions is a page a search
 * engine has to guess about, and guessing is what a canonical exists to stop.
 */
function writeMeta(by: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${by}="${key}"]`
  const existing = document.head.querySelector(selector)
  const element = existing ?? document.head.appendChild(document.createElement('meta'))
  element.setAttribute(by, key)
  element.setAttribute('content', content)
}

function removeMeta(key: string): void {
  document.head.querySelector(`meta[name="${key}"]`)?.remove()
}

function writeCanonical(href: string): void {
  const existing = document.head.querySelector('link[rel="canonical"]')
  const link = existing ?? document.head.appendChild(document.createElement('link'))
  link.setAttribute('rel', 'canonical')
  link.setAttribute('href', href)
}

function removeCanonical(): void {
  document.head.querySelector('link[rel="canonical"]')?.remove()
}

export function AppShell({ theme }: { theme: ThemeController }): React.JSX.Element {
  const { t } = useLocalisation()
  const width = useViewportWidth()
  const { pathname } = useLocation()
  const placement = navPlacement(width)
  // `titleFor` hands back a bundle key; the sentence is made here, where there is a translator. A
  // guide address naming no machine matches the guide's pattern but renders `NotFound`, so it is
  // named as what it is rather than "Machine guide" over "Nothing at this address" (S3-10).
  const unknownGuide = isUnknownGuide(pathname)
  const title = t(unknownGuide ? 'route.title.not-found' : titleFor(pathname))

  /**
   * Focus mode (M3-45): the machine gets the whole screen.
   *
   * On a phone in landscape the two rows this drops are 120 px of 390 — thirty-one per cent of the
   * screen — and landscape is the posture where a thumb reaches each flipper zone. The stage asks
   * for it; this renders it.
   *
   * **Through `usePlayFocus`, never a `useState` and a hand-rolled subscription** (M8-72). The
   * stage is a child of this shell, so its mount effect runs before this component's do. A
   * `useState` seeded from `isPlayFocused` here was read during render; the stage then set the
   * signal for a player whose stored choice is "on"; and the effect that subscribed arrived after
   * that. So the
   * shell drew both bars under a menu reading "Leave focus mode", on every load that mounted the
   * two together — the front door, a reload, a home-screen launch.
   */
  const focused = usePlayFocus()

  // **Left, rather than merely unmounted.** A player who ends a game inside focus mode, or
  // navigates away from the machine with the browser's own back gesture, must not arrive on the
  // arcade list with no navigation and no way out.
  useEffect(() => {
    return () => {
      setPlayFocus(false)
    }
  }, [pathname])

  /**
   * The document head, from one place (M3-57, S3-01, S3-02, S3-05).
   *
   * **One effect owns four things** — the title, the description, the canonical link and the
   * robots directive — because they are four answers to one question, *what page is this*, and
   * this file has already recorded what two writers on one piece of state costs.
   *
   * Why any of it is here rather than in `index.html`: Render answers every address with that one
   * file, so anything written into it is written on every page. A title there names the front door
   * on `/arcade`; a canonical there is an instruction to fold every real page into the front door.
   *
   * The order matters in one respect. `pageMeta` — what the mounted page says about itself, which
   * is how a machine page comes to be called `NEON NOVA` rather than `Play` — wins over the route
   * table's default, because only the page knows which machine it got.
   */
  const page = usePageMeta()
  // What a first-party machine's page will call itself, before its code arrives — so a cold load
  // keeps the prerendered title instead of flashing the route's generic one (`knownPageMeta`).
  const known = knownPageMeta(pathname)
  useEffect(() => {
    // Asked again here rather than closed over: it is a pure function of `pathname`, which is
    // already a dependency, and the object above is a new one on every render.
    const named = knownPageMeta(pathname)
    // No route copy for a guide with no machine: it is a not-found page, like any unknown address.
    const copy = isUnknownGuide(pathname) ? undefined : copyFor(pathname)
    const fallback = pathname === '/' ? 'HCC Pinball Arcade' : `${title} · HCC Pinball Arcade`
    document.title = page?.title ?? named?.title ?? copy?.title ?? fallback

    const description = page?.description ?? named?.description ?? copy?.description
    if (description !== undefined) writeMeta('name', 'description', description)

    // A route the sitemap does not offer gets no canonical — `canonicalFor` decides, and it
    // answers `undefined` for an address that is not a route at all. The element is *removed*
    // rather than left behind: the shell is not reloaded between routes, so a stale canonical
    // would follow the player onto a page that must not claim to be one.
    const canonical = canonicalFor(pathname)
    if (canonical === undefined) removeCanonical()
    else writeCanonical(canonical)

    // **`noindex` on an address that is not a route.** Render's SPA rewrite answers every unknown
    // path with a 200 and this shell, so a typo, a stale external link or an address from a future
    // milestone is an indexable page that says nothing — a soft 404, which is what a search
    // console reports it as. `titleFor` returning the not-found key is the same test the router
    // uses to render `NotFound`, so the two cannot disagree. A guide address naming no machine is
    // the same case one level down (S3-10): `MachineGuideRoute` renders `NotFound` on exactly the
    // ids `isUnknownGuide` answers for.
    if (titleFor(pathname) === 'route.title.not-found' || isUnknownGuide(pathname)) {
      writeMeta('name', 'robots', 'noindex')
    } else removeMeta('robots')
  }, [pathname, title, page])

  return (
    <div className={focused ? 'shell shell--focused' : 'shell'}>
      {/*
        The skip link (M3-40). First in the tab order and invisible until it has focus, which is
        the whole of the pattern: a keyboard player landing on the page should not have to tab
        through a logo, a back button, a theme toggle and five navigation tabs before reaching
        the machine — and on the landing route the machine is the entire reason they are here.
      */}
      <a className="skip-link" href="#main">
        {t('shell.skip')}
      </a>
      {/*
        Both rows are **removed** in focus mode rather than hidden, which is the rule this file
        already follows for the desktop bottom bar: a hidden nav is still in the accessibility
        tree, so a keyboard player tabs through five destinations that are not on the screen.
        The way back out is the control the stage draws over the machine.
      */}
      {focused ? null : (
        <div className="topbar-row">
          {/*
            **The machine's name, where the page has one** (S5-03). `titleFor` answers the route
            *pattern* — `/play/:machineId` is "Play" — so the bar over NEON NOVA said Play, and so
            did the bar over IRON FATHOM. `pageMeta` is the page's own answer and wins here for the
            same reason it wins for the title: only the page knows which machine it got.
          */}
          <TopBar title={page?.h1 ?? known?.h1 ?? title} canGoBack={pathname !== '/'} theme={theme} />
          {placement === 'header' ? <BottomNav width={width} pathname={pathname} /> : null}
        </div>
      )}
      <main className="shell__main" id="main">
        <Outlet />
        {/*
          **Inside the scrolling region, and after the route** (S5-01). The shell is a three-row
          grid whose middle row is the only thing that scrolls, so a footer in a fourth row would
          be a permanent band of links across a phone. Here it is where a footer belongs: at the
          end of the page, reached by scrolling to the end of the page.

          Not in focus mode, for the reason both navigation rows are not: a player who has pulled
          the plunger has said they are playing.

          **And not on a machine route at all** (M8-78). Focus mode was the wrong axis for this one:
          a player who has *not* pressed it is still looking at a cabinet, and under that cabinet
          sat nine document links and a copyright line — reported from a phone as "all that stuff
          between the bottom of the pinball machine and the bottom navigation bar". It is the same
          category as the getting-started strip, which came off the machine page the same day.

          **What it costs, said plainly:** a crawler on `/` no longer finds Support, Privacy, Terms,
          About and How to play in the footer. They are still linked from `/arcade` and `/pricing`,
          which S5-02 gave the inbound links for, and from every document page — and the primary
          navigation on `/` still reaches the four sections. The front door is a machine, and a band
          of links under it is a band of links a player scrolls past to reach nothing.
        */}
        {focused || isMachineRoute(pathname) ? null : <SiteFooter />}
      </main>
      {placement === 'bottom' && !focused ? (
        <BottomNav width={width} pathname={pathname} />
      ) : null}
    </div>
  )
}
